import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { GuidService } from '../../../../project-valuation/services/guid.service';
import { WidgetBaseComponent } from '../../../widgets/widgetBase';
import { ReportDesignerPageComponent } from '../../report-designer-page/report-designer-page.component';

/**
 * Text&list section (multiple rows)
 */
@Component({
  selector: 'rd-textandlist-section',
  templateUrl: './rd-textandlist-section.component.html',
  styleUrls: ['./rd-textandlist-section.component.scss']
})
export class RdTextandListSectionComponent implements OnInit, OnDestroy {
  /**
   * The data
   */
  @Input() data: any;

  /**
   * The Section Name
   */
  @Input() sectionName: string;

  /**
   * Should custom labels be enabled/disabled
   */
  @Input() disableCustomLabels = true;

  /**
   * The ref lists
   */
  @Input() reflists: any[];

  /**
   * Currency
   */
  @Input() currency: string;

  /**
   * Unit of area
   */
  @Input() unitOfArea: string;

  /**
   * Topic ref list (list of row titles)
   */
  @Input() topicReflist: string;

  /**
   * Edit mode
   */
  @Input() editMode: boolean;

  /**
   * The rows
   */
  @Input() rows: any[];

  /**
   * Colspan for the left (topic) column
   */
  @Input() leftColspan = 5;

  /**
   * Colspan for the right (value) column
   */
  @Input() rightColspan = 28;

  /**
   * Indicates if an alternating color scheme should be applied via CSS
   */
  @Input() alternatingColorScheme: boolean;

  /**
   * Page where widget is inserted
   */
  @Input() page: ReportDesignerPageComponent;

  /**
   * WidgetId
   */
  @Input() widgetId: string;

  /**
   * Selected hashtag
   */
  @Input() selectedTag: any;

  /**
   * Event handler for deleting the current row
   */
  @Input() deleteCurrentRow: Observable<void>;

  /**
   * Event handler for adding a new row
   */
  @Input() addRow: Observable<any>;

  /**
   * Event handler for moving the current row one up
   */
  @Input() moveCurrentRowUp: Observable<void>;

  /**
   * Event handler for moving the current row one down
   */
  @Input() moveCurrentRowDown: Observable<void>;

  /**
   * Right columns template
   */
  @Input() public rightColumnsTemplate!: TemplateRef<any>;

  /**
   * The widget
   */
  @Input() widget: WidgetBaseComponent;

  /**
   * Data changed event emitter - use for change & dirty dectection in widges
   */
  @Output() dataChanged = new EventEmitter<void>();

  /**
   * Section row count changed event emitter - use to detect if topics can be added to section
   */
  @Output() sectionRowsCountChanged = new EventEmitter<string>();

  /**
   * The current row index
   */
  currentRowIndex: number = null;

  /**
   * Subscriptions
   */
  private subscriptions: Subscription;

  /**
   * Custom Labels Definitions Object (for easier Angular binding)
   */
  private customLabelsDefinitionsObject: any = {};

  /**
   * Dropdown popup height settings
   */
  get popupSettings() {
    return {
      height: this.listHeight
    }
  }

  /**
   * Dropdown popup listHeight value
   */
  get listHeight() {
    return this.rows.length * 28;
  }

  /**
   * This RdTextandListSectionComponent instance
   */
  get textAndListInstance(): this {
    return this;
  }

  /**
   * Constructor
   * @param cdr
   * @param guidService
   */
  constructor(private cdr: ChangeDetectorRef, private guidService: GuidService) { }

  /**
   * Initializes the component
   */
  ngOnInit() {
    this.subscriptions = this.addRow.subscribe((item) => {
      this.handleAddRow(item);
    });

    this.subscriptions.add(this.deleteCurrentRow.subscribe(() => {
      this.handleDeleteCurrentRow();
    }));

    this.subscriptions.add(this.moveCurrentRowUp.subscribe(() => {
      this.handleMoveCurrentRowUp();
    }));

    this.subscriptions.add(this.moveCurrentRowDown.subscribe(() => {
      this.handleMoveCurrentRowDown();
    }));

    //We don't want to change the row id itself
    for (const row of this.rows) {
      row.lastTopicId = row.topicId;
    }

    //setup custom labels definitions
    this.data.customLabelsDefinitions = this.data.customLabelsDefinitions || [];
    //also set as property of customLabelsDefinitionsObject for easier Angular binding
    for (const definition of this.data.customLabelsDefinitions) {
      const fieldName = definition.fieldName;
      this.customLabelsDefinitionsObject[fieldName] = definition;
    }
  }

  /**
   * Handle moving the current row down
   */
  private handleMoveCurrentRowDown() {
    if (this.currentRowIndex !== null && !this.rows[this.currentRowIndex].isCustom) {
      this.rows.splice(this.currentRowIndex + 1, 0, this.rows.splice(this.currentRowIndex, 1)[0]);

      this.currentRowIndex = null;
    }
  }

  /**
   * Handle moving the current row up
   */
  private handleMoveCurrentRowUp() {
    if (this.currentRowIndex !== null && !this.rows[this.currentRowIndex].isCustom) {
      this.rows.splice(this.currentRowIndex - 1, 0, this.rows.splice(this.currentRowIndex, 1)[0]);

      this.currentRowIndex = null;
    }
  }

  /**
   * Handle adding a row
   * @param item
   */
  private handleAddRow(item: any) {
    if (item) {
      item.manuallyAdded = true;
      const unselectedFields = this.rows.filter(item => !item.selected);
      if (unselectedFields.length > 0) {
        const nextTopicId = Math.min(...unselectedFields.map(item => item.topicId));
        const nextRow = this.rows.filter(item => item.topicId == nextTopicId);
        nextRow[0].selected = true;

        this.sectionRowsCountChanged.emit(this.sectionName);
      }
    }
  }

  /**
   * Handle deleting the current row
   */
  private handleDeleteCurrentRow() {
    if (this.currentRowIndex !== null && !this.rows[this.currentRowIndex].isCustom) {
      const rowToDelete = this.rows[this.currentRowIndex];
      rowToDelete.selected = false;

      if (rowToDelete.isCustomLabelRow) {
        //if row is custom label row remove from definitions
        const fieldName = rowToDelete.fieldName;
        const definition = this.data.customLabelsDefinitions.find(d => d.fieldName === fieldName);
        if (definition) {
          const idx = this.data.customLabelsDefinitions.indexOf(definition);
          if (idx !== -1) {
            this.data.customLabelsDefinitions.splice(idx, 1);
            this.setDirtyFlag(true);
          }
        }
        delete this.customLabelsDefinitionsObject[fieldName];
      }

      this.rows.splice(0, 0, this.rows.splice(this.currentRowIndex, 1)[0]);
      this.currentRowIndex = null;

      this.sectionRowsCountChanged.emit(this.sectionName);
    }
  }

  /**
   * Destroys the component
   */
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Sets the dirty flag. Fires dataChanged
   * @param _value Dirty flag value
   */
  public setDirtyFlag(_value: boolean): void {
    this.dataChanged.next();
  }

  /**
   * Stops editing the current row
   */
  public stopEditRow(): void {
    this.currentRowIndex = null;
  }

  /**
   * Starts editing the row
   * @param index Indes of the row
   */
  public editRow(index: number): void {
    if (this.editMode) {
      this.currentRowIndex = index;
    }
    else {
      this.currentRowIndex = null;
    }
  }

  /**
   * Called if a row's topic was changed. Filters available topics etc
   * @param row The row
   * @param newTopicId The new topic ID
   */
  public onRowTopicChanged(row: any, newTopicId: number): void {
    //We don't want to change the row id itself
    this.cdr.detectChanges();
    row.topicId = row.lastTopicId;

    if (newTopicId === 0 && row.manuallyAdded && !this.disableCustomLabels) {
      //Task 14937: Text & List Widgets - Insert of custom labels
      // only if row was added manually
      this.handleCreateManualLabel(row);
    } else {
      // label/topic was changed to something
      const availableRow = this.rows.filter((en) => en.topicId === newTopicId && !en.selected);

      if (availableRow.length && newTopicId !== row.lastTopicId) {
        const indexToInsert = this.rows.indexOf(row);
        row.selected = false;

        const newSelectedRow = this.rows.filter((en) => {
          return en.topicId === newTopicId;
        })[0];
        const newSelectedRowIndex = this.rows.indexOf(newSelectedRow);

        newSelectedRow.selected = true;

        this.rows.splice(indexToInsert, 0, this.rows.splice(newSelectedRowIndex, 1)[0]);
      }
    }

    this.dataChanged.next();
  }

  /**
   * Checks if topic selection is disabled
   * @param self this (because this context is lost here)
   */
  public wrapIsTopicSelectionDisabled(self: RdTextandListSectionComponent) {
    return (itemArgs: { dataItem: any; index: number }): boolean => {
      if (!self.currentRowIndex) {
        return true;
      }
      if (self.rows[self.currentRowIndex].topicId === itemArgs.dataItem.oid) {
        //The current entry can always be selected
        return false;
      }
      if (itemArgs.dataItem.oid === 0 || self.rows.filter((en) => {
        return en.topicId === itemArgs.dataItem.oid && en.selected;
      }).length) {
        //Only non-empty and non-selected entries can be picked
        return true;
      }
      return false;
    }
  }

  /**
   * Checks if the row has an even index
   * @param row The row
   * @param index The index
   */
  public isEvenIndex(row: any, index: number): boolean {
    if (!row.selected) {
      return false;
    }

    let counter = 0;
    for (let i = 0; i < index; ++i) {
      if (this.rows[i].selected) {
        ++counter;
      }
    }
    return counter % 2 === 0;
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index Index
   * @param element Element
   */
  public trackRow(_index, element) {
    return element.topicId + element.fieldName;
  }

  /**
   * Handle manual label
   * @param row Row
   */
  private handleCreateManualLabel(row): void {
    row.isCustomLabelRow = true;
    row.topicId = -1;
    row.lastTopicId = -1;
    row.isTextField = true;
    row.fieldName = this.guidService.generate().toUpperCase();

    this.data.customLabelsDefinitions = this.data.customLabelsDefinitions || [];
    const definition = {
      sectionName: this.sectionName,
      fieldName: row.fieldName,
      customLabel: '',
      dataType: 1,// for now only string (type 1)
      stringValue: ''
    }

    this.data.customLabelsDefinitions.push(definition);
    //also set as property for easier Angular binding
    this.customLabelsDefinitionsObject[row.fieldName] = definition;

    //reset (delete properties for) other types if set
    delete row.isDateField;
    delete row.isNumericField;
    delete row.isSingleSelectField;
    delete row.isCustom;
  }

  /**
   * Gets row css classes
   * @param row The row
   * @param index The index
   */
  public getRowCssClasses(row: any, index: number): string {
    let classes = [];

    if (this.alternatingColorScheme && this.isEvenIndex(row, index)) {
      classes.push('light-grey-line');
    }

    if (row.cssClasses) {
      classes = [...classes, ...row.cssClasses];
    }

    return classes.join(' ');
  }

  /**
   * Gets right column align (view mode)
   * @param row The row
   */
  public getRightColumnAlign(row: any): string {
    return row.rightColumnAlign || 'right';
  }

  /**
   * Gets maxlength attribute value
   * @param row The row
   */
  public getMaxLength(row: any): number {
    return row.maxlength || 255;
  }

  /**
   * Gets maxlength attribute value
   * @param row The row
   */
  public getTextAreaRows(row: any): number {
    return row.textAreaRows || 1;
  }

  /**
   * Gets TextArea content (replace \n with <br> )
   * @param row The row
   */
  public getTextAreaHtml(content: string): string {
    return (content || '').replace(/(?:\r\n|\r|\n)/g, '<br>');
  }
}
